import View from '@play-co/timestep-core/lib/ui/View';
import PopupBasic from 'src/game/components/popups/PopupBasic';
import { Feature } from 'src/replicant/analytics';
import { CardsHeader, CardsHeaderState } from '../components/CardsHeader';
import { CardsList, CardsListState } from '../components/CardsList';
import { AssetGroup } from '@play-co/timestep-core';
import { ImageScaleView } from '@play-co/timestep-core/ui';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import sounds from 'src/lib/sounds';
import { cardsGetCollections } from 'src/replicant/cards/cards.getters';

/** Direction requested when navigating between card collections. */
type CardsCollectionNavigationDirection = 'previous' | 'next';

/** Close handler for the cards popup, optionally requesting another collection. */
type PopupCardsClose = (direction?: CardsCollectionNavigationDirection) => void;

/** Vertical centers for the decorative album bindings. */
const DETAILS_BINDING_Y = [190, 860];

/** Width of the navigation controls beneath the collection popup. */
const NAVIGATION_WIDTH = 453;

/** Shared height for the navigation controls and arrow buttons. */
const NAVIGATION_HEIGHT = 74;

/** Width of each collection navigation arrow. */
const NAVIGATION_BUTTON_WIDTH = 77;

/** Displays the cards in one collection and lets the player browse the album. */
export default class PopupCards extends PopupBasic<PopupCardsClose> {
  /** Header containing the collection title and progress. */
  private header: CardsHeader;

  /** Scrollable cards belonging to the current collection. */
  private cardsList: CardsList;

  /** Current collection position within the ordered album. */
  private pageCounter: LangBitmapFontTextShadowView;

  /**
   * Create the collection popup and its navigation controls.
   *
   * @param opts Popup superview and typed close handler.
   */
  constructor(opts: { superview: View; close: PopupCardsClose }) {
    // Configure the shared popup frame and regular close behavior.
    super({
      superview: opts.superview,
      boxOpts: {
        image: 'assets/ui/cards/common/popup_bg.png',
        scaleMethod: '9slice',
        sourceSlices: {
          horizontal: { left: 80, right: 80 },
          vertical: { top: 80, bottom: 80 },
        },
      },
      skipTitle: true,
      skipMessage: true,
      width: 670,
      height: 1050,
      offsetY: 26,
      close: () => {
        // A regular close exits without requesting another collection.
        opts.close();
      },
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.COLLECTION,
      },
      zIndex: 10000,
    });

    // Add the two album bindings along the left edge of the popup.
    for (const y of DETAILS_BINDING_Y) {
      new ImageScaleView({
        superview: this.box,
        image: 'assets/ui/cards/common/details_binding.png',
        width: 50,
        height: 79,
        x: 12,
        y,
        centerOnOrigin: true,
        scaleMethod: 'contain',
      });
    }

    // Create the collection title and progress header.
    this.header = new CardsHeader({
      superview: this.box,
      width: this.box.style.width,
      y: 10,
      zIndex: 1,
    });

    // Use the remaining popup space for the three-row cards list.
    const listTop = this.header.style.y + this.header.style.height;
    const listBottomPadding = 40;
    const listHeight = this.box.style.height - listTop - listBottomPadding;
    const listWidth = this.box.style.width * 0.9;

    this.cardsList = new CardsList({
      superview: this.box,
      x: this.box.style.width / 2 - listWidth / 2,
      y: listTop,
      width: listWidth,
      height: listHeight,
    });

    // Center the navigation row directly beneath the popup frame.
    const navigation = new View({
      superview: this.box,
      x: (this.box.style.width - NAVIGATION_WIDTH) / 2,
      y: this.box.style.height + 7,
      width: NAVIGATION_WIDTH,
      height: NAVIGATION_HEIGHT,
    });

    // The left arrow requests the previous collection from the opener.
    new ButtonScaleView({
      superview: navigation,
      image: 'assets/ui/shared/buttons/arrow.png',
      imagePressed: 'assets/ui/shared/buttons/arrow_pressed.png',
      width: NAVIGATION_BUTTON_WIDTH,
      height: NAVIGATION_HEIGHT,
      onClick: async () => {
        // Play the standard confirmation sound for collection navigation.
        sounds.sfx.play('ok');

        // Close with the direction so the opener can prepare the next popup.
        opts.close('previous');
      },
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: `${Feature.CARDS.COLLECTION}-previous`,
      },
    });

    // Show the current one-based collection position between both arrows.
    this.pageCounter = new LangBitmapFontTextShadowView({
      superview: navigation,
      x: NAVIGATION_BUTTON_WIDTH,
      width: NAVIGATION_WIDTH - NAVIGATION_BUTTON_WIDTH * 2,
      height: NAVIGATION_HEIGHT,
      align: 'center',
      verticalAlign: 'center',
      size: 40,
      font: 'Title',
      color: '#FFFFFF',
      textShadowColor: '#000000',
      textShadowOffsetX: 0,
      textShadowOffsetY: 3,
      canHandleEvents: false,
    });

    // The right arrow mirrors the left arrow and requests the next collection.
    new ButtonScaleView({
      superview: navigation,
      image: 'assets/ui/shared/buttons/arrow.png',
      imagePressed: 'assets/ui/shared/buttons/arrow_pressed.png',
      x: NAVIGATION_WIDTH - NAVIGATION_BUTTON_WIDTH,
      width: NAVIGATION_BUTTON_WIDTH,
      height: NAVIGATION_HEIGHT,
      flipX: true,
      onClick: async () => {
        // Play the standard confirmation sound for collection navigation.
        sounds.sfx.play('ok');

        // Close with the direction so the opener can prepare the next popup.
        opts.close('next');
      },
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: `${Feature.CARDS.COLLECTION}-next`,
      },
    });
  }

  /** Return the card and navigation assets loaded before the popup appears. */
  protected getAssetsGroup(): AssetGroup {
    // Load the two shared arrow states alongside the existing cards assets.
    return new AssetGroup([
      ...AssetGroup.constructURLs(['assets/ui/cards/common']),
      'assets/ui/shared/buttons/arrow.png',
      'assets/ui/shared/buttons/arrow_pressed.png',
    ]);
  }

  /**
   * Populate the popup with one collection's progress and cards.
   *
   * @param state Collection header and card list state.
   */
  public init(state: CardsHeaderState & CardsListState): void {
    // Start the popup asset loading and entrance animation.
    super.init({});

    // Derive the page number from the same ordering as the collection icons.
    const collectionIds = cardsGetCollections();
    const collectionNumber = collectionIds.indexOf(state.id) + 1;
    this.pageCounter.localeText = () =>
      `${collectionNumber}/${collectionIds.length}`;

    // Apply the current collection state to both popup content sections.
    this.header.setState(state);
    this.cardsList.setState(state);
  }
}
